import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import _ from 'lodash'
import moment from 'moment'
import { Views } from 'react-big-calendar'
import { MuiThemeProvider } from '@material-ui/core/styles'
import '../style.css'
import '../transitions.css'
import '../views/Calendar/TimelineView/timeline.css'
import '../views/Calendar/react-day-picker.css'
import 'handsontable/dist/handsontable.full.css'
import { withApollo } from '@apollo/client/react/hoc'

import { ThemeProvider } from 'styled-components'
import { withMsal as withAuth0 } from '../components/MsalAuth/withMsal'
import theme, { ksTheme } from '../theme'

// Components
import DataSync from '../components/DataSync'
import Application from '../components/Application'

// Mutations
import useTimeEntryMutations from '../hooks/mutations/useTimeEntryMutations'

// Sync Funcs
import {
  syncDelegateAccess,
  syncOutlookEvents,
  syncOutlookEmails,
} from '../components/DataSync/syncFunc'

// React Query
import queryClient from '../queryClient'
import {
  PROJECTS_QUERY_KEY,
  TIME_ENTRIES_QUERY_KEY,
  TIMESHEETS_QUERY_KEY,
  TIME_OFF_ENTRIES_QUERY_KEY,
  TIME_OFF_CODES_QUERY_KEY,
  OUTLOOK_EMAILS_QUERY_KEY,
  OUTLOOK_EVENTS_QUERY_KEY,
} from '../hooks/queries/keys'

// Utils
import timeUtils from '../utils/timeUtils'
import {
  getDefaultSyncDateRangeISOs,
  parseStartDateAsUTC,
  parseEndDateAsUTC,
} from '../server/utils/date'
import { getFreeGap } from '../views/Calendar/utils'
import { buildCurrentEditableEntry, buildPlaceholderEntry } from '../utils/appUtils'

import { mapStateToProps, mapDispatchToProps } from './store'
import {
  syncSelectedDelegateIdToStore,
  persistSelectedDelegateIdToStorage,
} from './handleSelectedDelegateId.helpers'
import { getDefaultProjectIdFromCache } from './setInitialCurrentEditableEntry.helpers'

import { logError, log } from '../utils/logger'

// SocketIO
import { withSocketIO } from '../components/SocketIO'

import { initialState } from './constants'

const { DAY: VIEW_PERIOD_DAY } = Views

const ApplicationWithMutations = props => {
  const { createEntry, createEntries } = useTimeEntryMutations({
    clearCurrentEditableEntry: props.clearCurrentEditableEntry,
  })
  return <Application {...props} createEntry={createEntry} createEntries={createEntries} />
}

ApplicationWithMutations.propTypes = {
  clearCurrentEditableEntry: PropTypes.func.isRequired,
}

class App extends React.Component {
  state = initialState

  setInitialCurrentEditableEntry = (cb, shouldClearEntry) => {
    const stateCurrentEditableEntry = this.state.currentEditableEntry
    const allTimeEntries = _.flatten(
      queryClient.getQueriesData(TIME_ENTRIES_QUERY_KEY).map(([, data]) => data ?? []),
    )
    const selectedDayEntries = allTimeEntries.filter(entry => {
      const { start, end } = entry

      const isEntryInSelectedDate =
        timeUtils.areSameDay(start, this.state.selectedDate) &&
        timeUtils.areSameDay(end, this.state.selectedDate)
      return isEntryInSelectedDate
    })

    const defaultFreeGap = {
      start: moment(this.state.selectedDate, 'ddd MMM D YYYY HH:mm:ss ZZ')
        .set({ h: 8, m: 0 })
        .toISOString(),
      end: moment(this.state.selectedDate, 'ddd MMM D YYYY HH:mm:ss ZZ')
        .set({ h: 8, m: 30 })
        .toISOString(),
    }

    const freeGap =
      getFreeGap({ selectedDate: this.state.selectedDate, selectedDayEntries }) ?? defaultFreeGap

    const entry = buildPlaceholderEntry({
      resourceId: 1,
      start: freeGap.start,
      end: freeGap.end,
    })

    const allProjects = _.flatten(
      queryClient.getQueriesData(PROJECTS_QUERY_KEY).map(([, data]) => data ?? []),
    )
    const defaultProjectId = getDefaultProjectIdFromCache(queryClient)
    const currentEditableEntry = buildCurrentEditableEntry({
      entry,
      projects: allProjects,
      defaultProjectId,
    })

    const entryToState =
      _.isEqual(stateCurrentEditableEntry, {}) || shouldClearEntry
        ? {
            ...currentEditableEntry,
            hideOnWeekPeriod: true,
          }
        : {
            ...stateCurrentEditableEntry,
            start: new Date(freeGap.start),
            end: new Date(freeGap.end),
          }

    this.setState(
      {
        currentEditableEntry: entryToState,
        initialCurrentEditableEntry: entryToState,
      },
      cb,
    )
  }

  startInitialDelegateAccessSync = async () => {
    const syncData = {
      handleAppState: this.handleAppState,
      client: this.props.client,
      shouldRefresh: true,
    }
    return syncDelegateAccess(syncData).then()
  }

  getUIRefreshTimePeriod = (shouldIncludeYesterday = false) => {
    try {
      const hasToFetchAllWeekData = !this.isDayViewPeriodSelected()
      const selectedDayDate = moment(this.state.selectedDate)

      if (!hasToFetchAllWeekData) {
        const yesterdayStart = selectedDayDate
          .clone()
          .subtract(shouldIncludeYesterday ? 1 : 0, 'day')
          .startOf('day')
        const todayEnd = selectedDayDate.clone().endOf('day')

        return {
          startISO: parseStartDateAsUTC(yesterdayStart),
          endISO: parseEndDateAsUTC(todayEnd),
        }
      }

      const weekMonday = selectedDayDate
        .clone()
        .weekday(1)
        .subtract(1, 'day')
        .startOf('day')
      const weekSunday = selectedDayDate
        .clone()
        .weekday(7)
        .subtract(1, 'day')
        .endOf('day')

      return {
        startISO: parseStartDateAsUTC(weekMonday),
        endISO: parseEndDateAsUTC(weekSunday),
      }
    } catch (error) {
      logError(error)
    }
  }

  updateView = async () => {
    await queryClient.invalidateQueries({ queryKey: TIMESHEETS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: OUTLOOK_EMAILS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: OUTLOOK_EVENTS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: TIME_OFF_CODES_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY })
    await queryClient.invalidateQueries({ queryKey: TIME_ENTRIES_QUERY_KEY })
    log(`LS → UI → updateView complete`)
  }

  setToInitialState = () => this.setState(initialState)

  updateViewData = async () => {
    try {
      const { startISO, endISO } = this.getUIRefreshTimePeriod(true)

      if (this.props.isConnected) {
        await queryClient.invalidateQueries({ queryKey: TIMESHEETS_QUERY_KEY })

        const doesOutsideContainInside = timeUtils.doesTimePeriodContainsAnother(
          { startISO, endISO },
          getDefaultSyncDateRangeISOs(),
        )

        if (!doesOutsideContainInside) {
          await queryClient.invalidateQueries({ queryKey: TIME_ENTRIES_QUERY_KEY })
          await queryClient.invalidateQueries({ queryKey: TIME_OFF_ENTRIES_QUERY_KEY })
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: OUTLOOK_EMAILS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: OUTLOOK_EVENTS_QUERY_KEY }),
      ])

      if (!this.props.isConnected) return

      const doesOutsideContainInside = timeUtils.doesTimePeriodContainsAnother(
        { startISO, endISO },
        getDefaultSyncDateRangeISOs(),
      )

      if (!this.state.hasOutlookEverSynced || !doesOutsideContainInside) {
        const { startISO: outlookStartISO, endISO: outlookEndISO } = getDefaultSyncDateRangeISOs()
        const outlookSyncData = {
          selectedDelegateId: this.state.selectedDelegateId,
          handleAppState: this.handleAppState,
          client: this.props.client,
        }
        await syncOutlookEmails({
          ...outlookSyncData,
          startISO: outlookStartISO,
          endISO: outlookEndISO,
          isDelta: true,
          reSyncDelta: true,
        })
        await syncOutlookEvents({
          ...outlookSyncData,
          startISO: outlookStartISO,
          endISO: outlookEndISO,
          isDelta: true,
          reSyncDelta: true,
        })
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: OUTLOOK_EMAILS_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: OUTLOOK_EVENTS_QUERY_KEY }),
        ])
        this.setState({ hasOutlookEverSynced: true })
      }
    } catch (e) {
      logError(e)
    }
  }

  clearCurrentEditableEntry = cb => {
    this.setInitialCurrentEditableEntry(cb, true)
  }

  isDayViewPeriodSelected = () => this.state.viewPeriod === VIEW_PERIOD_DAY

  handleAppState = (newObj, asyncFn = () => {}) => {
    this.setState(newObj, asyncFn)
  }

  handleSelectedDelegateId = (selectedDelegateId, saveOnLocalStorage = false, extraProps = {}) => {
    const normalizedDelegateId = syncSelectedDelegateIdToStore(selectedDelegateId)

    this.setState({ ...extraProps, selectedDelegateId: normalizedDelegateId }, () => {
      if (saveOnLocalStorage) {
        persistSelectedDelegateIdToStorage(normalizedDelegateId)
      }
    })
  }

  render() {
    return (
      <ThemeProvider theme={ksTheme}>
        <MuiThemeProvider theme={theme}>
          <DataSync
            watchConnectivity={this.props.watchConnectivity}
            isConnected={this.props.isConnected}
            auth0={this.props.auth0}
            client={this.props.client}
            io={this.props.io}
            loginSuccess={this.props.loginSuccess}
            isUserLoggedIn={this.props.isUserLoggedIn}
            hasUserGotStarted={this.state.hasUserGotStarted}
            getUIRefreshTimePeriod={this.getUIRefreshTimePeriod}
            handleAppState={this.handleAppState}
            setToInitialState={this.setToInitialState}
            updateViewData={this.updateViewData}
            selectedDelegateId={this.state.selectedDelegateId}
            isRecurrentSyncInProgress={this.state.isRecurrentSyncInProgress}
            setInitialCurrentEditableEntry={this.setInitialCurrentEditableEntry}
            handleSelectedDelegateId={this.handleSelectedDelegateId}
            startInitialDelegateAccessSync={this.startInitialDelegateAccessSync}
            setIsTimesheetLoading={isLoading => this.setState({ isTimesheetLoading: isLoading })}
          />
          <ApplicationWithMutations
            isConnected={this.props.isConnected}
            needsReauth={this.props.needsReauth}
            client={this.props.client}
            isUserLoggedIn={this.props.isUserLoggedIn}
            userInfo={this.props.userInfo}
            handleAppState={this.handleAppState}
            updateViewData={this.updateViewData}
            updateView={this.updateView}
            getUIRefreshTimePeriod={this.getUIRefreshTimePeriod}
            modal={this.state.modal}
            isNoFreeGapModalOpen={this.state.isNoFreeGapModalOpen}
            selectedDate={this.state.selectedDate}
            csvUploadData={this.state.csvUploadData}
            viewPeriod={this.state.viewPeriod}
            selectedDelegateId={this.state.selectedDelegateId}
            currentEditableEntry={this.state.currentEditableEntry}
            view={this.state.view}
            isSubmitInProgress={this.state.isSubmitInProgress}
            isTimesheetLoading={this.state.isTimesheetLoading}
            isAllEventsButton={this.state.isAllEventsButton}
            isAddAllEntriesFromEventsModalOpen={this.state.isAddAllEntriesFromEventsModalOpen}
            areEventsBeingAdded={this.state.areEventsBeingAdded}
            successCsvInfoTotal={this.state.successCsvInfoTotal}
            failedCsvInfoTotal={this.state.failedCsvInfoTotal}
            csvActionButtonsBlocked={this.state.csvActionButtonsBlocked}
            isIssueReportModalFromMenuOpen={this.state.isIssueReportModalFromMenuOpen}
            isDelegateAccessModalFromMenuOpen={this.state.isDelegateAccessModalFromMenuOpen}
            hasUserGotStarted={this.state.hasUserGotStarted}
            clearCurrentEditableEntry={this.clearCurrentEditableEntry}
            setInitialCurrentEditableEntry={this.setInitialCurrentEditableEntry}
            initialCurrentEditableEntry={this.state.initialCurrentEditableEntry}
            handleSelectedDelegateId={this.handleSelectedDelegateId}
          />
        </MuiThemeProvider>
      </ThemeProvider>
    )
  }
}

App.propTypes = {
  isUserLoggedIn: PropTypes.bool.isRequired,
  userInfo: PropTypes.object.isRequired,
  io: PropTypes.object.isRequired,
  watchConnectivity: PropTypes.func.isRequired,
  isConnected: PropTypes.bool.isRequired,
  needsReauth: PropTypes.bool.isRequired,
  client: PropTypes.object.isRequired,
  auth0: PropTypes.object.isRequired,
  loginSuccess: PropTypes.func.isRequired,
}

export default withAuth0(
  withApollo(withSocketIO(connect(mapStateToProps, mapDispatchToProps)(App))),
)
